import styled, { css, keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const authSubmitSpin = keyframes`
	to {
		transform: rotate(360deg);
	}
`;

export const AuthOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 7000;
	display: grid;
	place-items: center;
	padding: 24px;
	background: rgba(0, 0, 0, 0.72);
	backdrop-filter: blur(8px);

	@media (max-width: 680px) {
		padding: 0;
		place-items: stretch;
		background: #050209;
		backdrop-filter: none;
	}
`;

export const AuthCard = styled.article`
	position: relative;
	width: min(1020px, 100%);
	height: min(540px, calc(100vh - 48px));
	display: block;
	border-radius: 28px;
	overflow: hidden;
	background: #050209;
	box-shadow:
		0 0 0 1.5px rgba(210, 80, 255, 0.65),
		0 0 32px rgba(180, 40, 255, 0.4),
		0 24px 80px rgba(0, 0, 0, 0.65);

	@media (max-width: 860px) {
		width: min(920px, 100%);
		height: min(540px, calc(100vh - 40px));
	}

	@media (max-width: 680px) {
		width: 100%;
		height: 100dvh;
		min-height: 100dvh;
		border-radius: 0;
		box-shadow: none;
	}
`;

export const AuthVisual = styled.section`
	position: absolute;
	inset: 0;
	z-index: 0;
	min-width: 0;
	min-height: 0;
	background: #12081a;
	overflow: hidden;
`;

const visualImageBase = css`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

export const AuthVisualWide = styled.img`
	${visualImageBase}
	object-position: 48% center;

	${(props) =>
		props.$blur &&
		css`
			z-index: 1;
			filter: blur(30px);
			transform: scale(1.14);
			transform-origin: center;
			-webkit-mask-image: linear-gradient(
				90deg,
				transparent 12%,
				rgba(0, 0, 0, 0.08) 28%,
				rgba(0, 0, 0, 0.35) 42%,
				rgba(0, 0, 0, 0.7) 58%,
				#000 78%
			);
			mask-image: linear-gradient(
				90deg,
				transparent 12%,
				rgba(0, 0, 0, 0.08) 28%,
				rgba(0, 0, 0, 0.35) 42%,
				rgba(0, 0, 0, 0.7) 58%,
				#000 78%
			);
		`}

	@media (max-width: 860px) {
		object-position: 45% center;
	}

	@media (max-width: 680px) {
		display: none;
	}
`;

export const AuthVisualTall = styled.img`
	${visualImageBase}
	display: none;
	object-position: 6% center;

	@media (max-width: 680px) {
		display: block;
		z-index: 0;
		object-fit: cover;
		object-position: center bottom;

		${(props) =>
			props.$blur &&
			css`
				display: none;
			`}
	}
`;

export const AuthVisualVeil = styled.div`
	display: block;
	position: absolute;
	inset: 0;
	z-index: 2;
	pointer-events: none;
	background: linear-gradient(
		90deg,
		transparent 0%,
		transparent 22%,
		rgba(5, 2, 9, 0.04) 40%,
		rgba(5, 2, 9, 0.14) 58%,
		rgba(5, 2, 9, 0.28) 100%
	);

	@media (max-width: 680px) {
		background: linear-gradient(
			180deg,
			rgba(5, 2, 9, 0.45) 0%,
			rgba(5, 2, 9, 0.22) 28%,
			rgba(5, 2, 9, 0.35) 52%,
			rgba(5, 2, 9, 0.28) 78%,
			rgba(5, 2, 9, 0.18) 100%
		);
	}
`;

export const AuthPanel = styled.section`
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 3;
	width: min(58%, 560px);
	min-width: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 26px 40px 24px;
	isolation: isolate;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		z-index: -1;
		pointer-events: none;
		background:
			radial-gradient(circle at 88% 10%, rgba(255, 60, 200, 0.07), transparent 42%),
			linear-gradient(
				90deg,
				transparent 0%,
				rgba(5, 2, 9, 0.2) 18%,
				rgba(5, 2, 9, 0.55) 48%,
				rgba(5, 2, 9, 0.72)
			);
		-webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 22%, #000 100%);
		mask-image: linear-gradient(90deg, transparent 0%, #000 22%, #000 100%);
	}

	@media (max-width: 860px) {
		width: min(58%, 480px);
		padding: 22px 22px 20px;
	}

	@media (max-width: 680px) {
		inset: 0;
		width: 100%;
		min-height: 0;
		overflow: auto;
		-webkit-overflow-scrolling: touch;
		padding: max(20px, env(safe-area-inset-top, 0px)) 22px
			calc(22px + env(safe-area-inset-bottom, 0px));
		justify-content: center;
		align-items: center;
		gap: 16px;

		&::before {
			background: none;
			-webkit-mask-image: none;
			mask-image: none;
		}
	}
`;

export const AuthChrome = styled.div`
	position: absolute;
	top: 10px;
	right: 12px;
	z-index: 4;
	display: flex;
	align-items: center;
	gap: 8px;

	@media (max-width: 680px) {
		top: max(12px, env(safe-area-inset-top, 0px));
		right: max(12px, env(safe-area-inset-right, 0px));
		z-index: 5;
	}
`;

export const AuthClose = styled.button`
	width: 28px;
	height: 28px;
	border: 0;
	padding: 0;
	background: transparent;
	font-size: 22px;
	line-height: 1;
	color: rgba(255, 255, 255, 0.45);
	cursor: pointer;

	&:hover {
		color: #fff;
	}

	@media (max-width: 680px) {
		width: 36px;
		height: 36px;
		font-size: 28px;
		color: rgba(255, 255, 255, 0.85);
	}
`;

export const AuthBrand = styled.div`
	position: relative;
	z-index: 1;
	text-align: center;
	margin-bottom: 18px;
	transform: ${(props) => (props.$isLogin ? 'translateY(-28px)' : 'translateY(-10px)')};

	@media (max-width: 680px) {
		margin: 0 0 8px;
		transform: none;
		width: 100%;
		max-width: 420px;
	}
`;

export const AuthLogo = styled.img`
	display: block;
	width: 108px;
	margin: 0 auto 6px;
	filter: drop-shadow(0 0 14px rgba(255, 77, 242, 0.4));

	@media (max-width: 680px) {
		width: 96px;
		margin-bottom: 8px;
	}
`;

export const AuthKicker = styled.p`
	margin: 0;
	font-size: 24px;
	font-weight: 800;
	letter-spacing: -0.03em;
	line-height: 1.1;
	background: linear-gradient(95deg, #f7e4c8 0%, #ffc9a8 28%, #ff9ec8 62%, #ff4db8 100%);
	-webkit-background-clip: text;
	background-clip: text;
	color: transparent;
	filter: drop-shadow(0 0 8px rgba(255, 120, 200, 0.4))
		drop-shadow(0 0 18px rgba(255, 70, 180, 0.25));

	@media (max-width: 680px) {
		font-size: 22px;
	}
`;

export const AuthHeadline = styled.h2`
	margin: 0;
	font-size: 42px;
	font-weight: 800;
	letter-spacing: -0.04em;
	line-height: 1.02;
	background: linear-gradient(95deg, #ff6bcb 0%, #ff3db8 38%, #c026ff 100%);
	-webkit-background-clip: text;
	background-clip: text;
	color: transparent;
	filter: drop-shadow(0 0 12px rgba(255, 77, 200, 0.55))
		drop-shadow(0 0 28px rgba(192, 38, 255, 0.35));

	@media (max-width: 860px) {
		font-size: 32px;
	}

	@media (max-width: 680px) {
		font-size: 34px;
	}
`;

export const AuthTagline = styled.p`
	margin: 6px 0 0;
	font-size: 13px;
	color: rgba(190, 170, 205, 0.75);
	text-shadow: 0 1px 12px rgba(0, 0, 0, 0.7);

	@media (max-width: 680px) {
		margin-top: 8px;
		font-size: 14px;
		line-height: 1.4;
		color: rgba(220, 205, 230, 0.88);
	}
`;

export const AuthInlineHeart = styled.span`
	display: none;
	vertical-align: -1px;
	margin-left: 2px;

	svg {
		filter: drop-shadow(0 0 6px rgba(255, 77, 242, 0.7));
	}
`;

export const AuthFormBlock = styled.div`
	position: relative;
	z-index: 1;
	display: flex;
	flex-direction: column;
	gap: 12px;

	@media (max-width: 680px) {
		gap: 18px;
		width: 100%;
		max-width: 420px;
	}
`;

export const AuthFormTop = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;

	@media (max-width: 860px) {
		flex-direction: column;
		gap: 6px;
	}

	@media (max-width: 680px) {
		gap: 8px;
	}
`;

export const AuthTitle = styled.h1`
	margin: 0 0 3px;
	font-size: 18px;
	font-weight: 800;
	color: #fff;
	text-shadow: 0 1px 12px rgba(0, 0, 0, 0.7);

	@media (max-width: 680px) {
		font-size: 22px;
		margin-bottom: 0;
	}
`;

export const AuthSwitchButton = styled.button`
	flex-shrink: 0;
	margin-top: 2px;
	border: 0;
	padding: 0;
	background: transparent;
	font-size: 11.5px;
	color: rgba(210, 195, 220, 0.7);
	text-align: right;
	white-space: nowrap;
	cursor: pointer;
	text-shadow: 0 1px 12px rgba(0, 0, 0, 0.7);

	span {
		color: #c86bff;
		font-weight: 700;
	}

	@media (max-width: 860px) {
		text-align: left;
	}

	@media (max-width: 680px) {
		margin-top: 0;
		font-size: 15px;
		line-height: 1.4;
		white-space: normal;
		color: rgba(220, 205, 230, 0.85);
	}
`;

export const AuthMain = styled.div`
	min-width: 0;

	@media (max-width: 680px) {
		width: 100%;
	}
`;

export const AuthFormRoot = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 0;
`;

export const AuthFormEl = styled.form`
	display: grid;
	gap: 10px;

	@media (max-width: 680px) {
		gap: 14px;
	}
`;

export const AuthFormFields = styled.div`
	display: grid;
	gap: 10px;

	@media (max-width: 680px) {
		gap: 14px;
	}
`;

export const AuthFormBottom = styled.div`
	margin-top: 2px;

	@media (max-width: 680px) {
		margin-top: 6px;
	}
`;

export const AuthError = styled.p`
	margin: 0;
	padding: 10px 12px;
	border-radius: 10px;
	border: 1px solid rgba(255, 77, 242, 0.28);
	background: rgba(255, 77, 242, 0.08);
	color: #ffb8f5;
	font-size: 0.82rem;
	line-height: 1.35;

	@media (max-width: 680px) {
		padding: 12px 14px;
		font-size: 0.92rem;
	}
`;

export const AuthSuccess = styled.p`
	margin: 0;
	padding: 12px 14px;
	border-radius: 10px;
	border: 1px solid rgba(84, 214, 161, 0.35);
	background: rgba(84, 214, 161, 0.1);
	color: #9ef0c8;
	font-size: 0.84rem;
	line-height: 1.45;
`;

export const AuthField = styled.div`
	position: relative;
	display: block;
`;

export const AuthLabel = styled.label`
	${(props) =>
		props.$visible
			? css`
					display: block;
					margin-bottom: 7px;
					font-size: 0.74rem;
					font-weight: 700;
					letter-spacing: 0.05em;
					text-transform: uppercase;
					color: rgba(255, 255, 255, 0.55);
				`
			: css`
					position: absolute;
					width: 1px;
					height: 1px;
					padding: 0;
					margin: -1px;
					overflow: hidden;
					clip: rect(0, 0, 0, 0);
					white-space: nowrap;
					border: 0;
				`}
`;

export const AuthFieldIcon = styled.span`
	position: absolute;
	left: 13px;
	top: 50%;
	transform: translateY(-50%);
	color: rgba(200, 185, 215, 0.9);
	display: grid;
	place-items: center;
	pointer-events: none;
	z-index: 2;

	@media (max-width: 680px) {
		left: 16px;
	}
`;

export const AuthInput = styled.input`
	width: 100%;
	height: 44px;
	padding: ${(props) => (props.$withIcon ? '0 42px' : '0 14px')};
	border-radius: 12px;
	border: 1px solid rgba(255, 255, 255, 0.16);
	background: rgba(10, 4, 18, 0.92);
	color: #fff;
	font-family: ${THEME.fontApp};
	font-size: 14px;
	outline: none;
	position: relative;
	z-index: 1;

	&::placeholder {
		color: rgba(170, 155, 185, 0.55);
	}

	&:focus {
		border-color: rgba(200, 100, 255, 0.55);
	}

	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}

	@media (max-width: 680px) {
		height: 54px;
		padding: ${(props) => (props.$withIcon ? '0 48px' : '0 16px')};
		border-radius: 14px;
		font-size: 16px;
		background: rgba(10, 4, 18, 0.88);
		border-color: rgba(255, 255, 255, 0.22);
	}
`;

export const AuthPasswordWrap = styled.div`
	position: relative;
	width: 100%;
`;

export const AuthPasswordInput = styled(AuthInput).attrs({ $withIcon: true })``;

export const AuthPasswordToggle = styled.button`
	position: absolute;
	right: 8px;
	top: 50%;
	transform: translateY(-50%);
	width: 32px;
	height: 32px;
	border: 0;
	border-radius: 8px;
	display: grid;
	place-items: center;
	color: rgba(200, 185, 215, 0.9);
	background: transparent;
	cursor: pointer;
	z-index: 2;

	&:hover:not(:disabled) {
		color: #fff;
	}

	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}

	@media (max-width: 680px) {
		right: 10px;
		width: 40px;
		height: 40px;
	}
`;

export const AuthHint = styled.p`
	display: flex;
	align-items: center;
	gap: 6px;
	margin: 2px 0 0;
	font-size: 11px;
	color: rgba(170, 155, 185, 0.55);
	text-shadow: 0 1px 12px rgba(0, 0, 0, 0.7);

	@media (max-width: 680px) {
		margin-top: 6px;
		font-size: 13px;
	}
`;

export const AuthSubmit = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	height: 46px;
	margin-top: 2px;
	border-radius: 999px;
	border: 1px solid rgba(255, 220, 255, 0.28);
	background: ${(props) =>
		props.$success
			? `linear-gradient(135deg, #2f9f72, ${THEME.green})`
			: 'linear-gradient(90deg, #8b1aff 0%, #c026ff 32%, #ff3db5 68%, #ff7a4d 100%)'};
	color: #fff;
	font-family: ${THEME.fontApp};
	font-size: 15px;
	font-weight: 800;
	letter-spacing: 0.01em;
	cursor: pointer;
	box-shadow: ${(props) =>
		props.$success
			? '0 10px 28px rgba(84, 214, 161, 0.3)'
			: `0 0 0 1px rgba(255, 120, 220, 0.1),
				0 0 14px rgba(160, 40, 255, 0.4),
				0 0 28px rgba(255, 70, 180, 0.24),
				0 0 36px rgba(255, 110, 70, 0.14),
				inset 0 1px 0 rgba(255, 255, 255, 0.32),
				inset 0 -1px 0 rgba(80, 0, 60, 0.16)`};
	position: relative;
	z-index: 1;
	transition:
		filter 160ms ease,
		box-shadow 160ms ease,
		opacity 160ms ease;

	&:hover:not(:disabled) {
		filter: brightness(1.06);
	}

	&:disabled {
		opacity: ${(props) => (props.$success ? 1 : 0.6)};
		cursor: not-allowed;
	}

	@media (max-width: 680px) {
		height: 56px;
		margin-top: 4px;
		font-size: 17px;
	}
`;

export const AuthSubmitContent = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
`;

export const AuthSubmitSpinner = styled.span`
	width: 16px;
	height: 16px;
	border: 2px solid rgba(255, 255, 255, 0.35);
	border-top-color: #fff;
	border-radius: 50%;
	animation: ${authSubmitSpin} 0.8s linear infinite;
`;

export const AuthLinks = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px 16px;
	justify-content: flex-start;
	margin-top: 2px;
`;

export const AuthLinkButton = styled.button`
	border: 0;
	background: transparent;
	padding: 0;
	color: rgba(190, 170, 205, 0.78);
	font-family: ${THEME.fontApp};
	font-size: 0.8rem;
	text-decoration: underline;
	text-underline-offset: 3px;
	cursor: pointer;

	&:hover {
		color: #fff;
	}

	@media (max-width: 680px) {
		font-size: 0.95rem;
		padding: 6px 0;
	}
`;

export const ResetOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 7100;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(3, 1, 5, 0.72);
	backdrop-filter: blur(20px) saturate(1.08);
`;

export const ResetCard = styled.div`
	position: relative;
	width: min(100%, 400px);
	padding: 24px 24px 22px;
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 18px;
	background:
		radial-gradient(circle at 80% 0%, rgba(255, 77, 242, 0.12), transparent 38%),
		rgba(18, 7, 20, 0.98);
	box-shadow:
		0 28px 88px rgba(0, 0, 0, 0.72),
		0 0 28px rgba(255, 77, 242, 0.1);
`;

export const ResetClose = styled.button`
	position: absolute;
	top: 14px;
	right: 14px;
	width: 34px;
	height: 34px;
	border: 0;
	border-radius: 50%;
	color: rgba(255, 255, 255, 0.66);
	background: transparent;
	font-size: 1.6rem;
	line-height: 1;
	cursor: pointer;

	&:hover {
		color: #fff;
		background: rgba(255, 255, 255, 0.06);
	}
`;

export const ResetHeader = styled.header`
	padding-right: 36px;
	margin-bottom: 18px;

	h3 {
		margin: 0 0 6px;
		font-family: ${THEME.fontDisplay};
		font-size: 1.15rem;
		line-height: 1.2;
		color: ${THEME.text};
	}

	p {
		margin: 0;
		font-size: 0.82rem;
		line-height: 1.45;
		color: rgba(255, 255, 255, 0.7);
	}
`;

export const ResetForm = styled.form`
	display: flex;
	flex-direction: column;
`;

export const ResetSubmit = styled(AuthSubmit)`
	margin-top: 18px;
`;

export const ResetBack = styled.button`
	margin-top: 14px;
	border: 0;
	background: transparent;
	padding: 0;
	align-self: center;
	color: ${THEME.soft};
	font-family: ${THEME.fontApp};
	font-size: 0.82rem;
	text-decoration: underline;
	text-underline-offset: 3px;
	cursor: pointer;

	&:hover {
		color: ${THEME.text};
	}
`;
